// Global audio performance logger for the dashboard
class AudioPerformanceLogger {
  constructor() {
    this.logHandlers = new Set();
    this.isEnabled = false;
    
    // Electron-specific throttling to prevent crash
    this.isElectron = typeof window !== 'undefined' && window.electronAPI;
    this.lastLogTime = new Map(); // Track last log time per category
    this.logThrottle = this.isElectron ? 2000 : 500; // 2 seconds in Electron, 500ms in web
  }

  // Register a log handler (used by the dashboard)
  registerHandler(handler) {
    this.logHandlers.add(handler);
    this.isEnabled = this.logHandlers.size > 0;
  }

  // Unregister a log handler
  unregisterHandler(handler) {
    this.logHandlers.delete(handler);
    this.isEnabled = this.logHandlers.size > 0;
  }

  // Log an event (called by other components) - with throttling
  log(level, category, message, data = null) {
    if (!this.isEnabled) return;
    
    // Throttle high-frequency audio logs in Electron
    if (this.isElectron && category === 'audio' && level === 'debug') {
      const now = Date.now();
      const lastTime = this.lastLogTime.get('audio_debug') || 0;
      if (now - lastTime < this.logThrottle) {
        return; // Skip this log to reduce frequency
      }
      this.lastLogTime.set('audio_debug', now);
    }
    
    this.logHandlers.forEach(handler => {
      try {
        handler(level, category, message, data);
      } catch (error) {
        console.warn('Error in audio performance log handler:', error);
      }
    });
  }

  // Convenience methods
  info(category, message, data) {
    this.log('info', category, message, data);
  }

  warn(category, message, data) {
    this.log('warn', category, message, data);
  }

  error(category, message, data) {
    this.log('error', category, message, data);
  }

  debug(category, message, data) {
    this.log('debug', category, message, data);
  }
}

// Global instance
export const audioPerformanceLogger = new AudioPerformanceLogger();

// Convenience hook for components to use the logger
export function useAudioPerformanceLogger() {
  return audioPerformanceLogger;
}
